import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { OverlayModule } from '@angular/cdk/overlay';
import { StoreModule } from '@ngrx/store';

import * as Api from './store/apis';
import * as Service from './services';
import { CoreRoute } from './route';
import { Analytics } from './services/analytics/analytics';
import { metaReducers, reducers } from './store/reducers';
import { CONFIG_TOKEN, REWRITE_TOKEN } from './constants';

import { ModalModule } from 'shared/modal/module';
import { IXMOptions } from './interfaces';
import { Util } from './services/util';

import { ApiResourceInterceptor } from 'services/http/resource';
import { ShareRequestInterceptor } from 'services/http/share';
import { TimeoutInterceptor } from 'services/http/timeout';
import { GenericErrorInterceptor } from 'services/http/generic-error';
import { CmsErrorInterceptor } from 'services/http/cms-error';

/**
 * We need to create listeners before UI Router is initialized so hooks (ex. onBefore, onStart) work immediately without delay
 *
 * Router initialization happens in the `initializeRouter` function below.
*/
export function initRouterListeners(analytics: Service.Analytics, coreRoute: CoreRoute): () => void {
    return () => {
        analytics.setupRouterListener();
        coreRoute.setupRouterListener();
    };
}

export function redirectOrReplaceUrl(options: IXMOptions, rewriteRules: CmsRewriteRules, seo: Service.Seo): () => Promise<void> {
    return () => new Promise((resolve: () => void) => {
        const baseUrl: string = `${location.protocol}//${location.host}${options.BASE_URL}`;
        let currentUrl: string = location.href.replace(baseUrl, '');
        let isExternalUrl: boolean;
        let trackingParam: string;
        const querySearchRegex: string = '([?].+)?';

        const newURLFound: boolean = rewriteRules.rules.reduce((replace: boolean, rule: RewriteRule) => {

            if (rule.type === 'regex' && rule.replaceUrl) {
                rule.searchValue = rule.searchValue.endsWith('$') ? `${rule.searchValue.split('$')[0]}${querySearchRegex}$` : `${rule.searchValue}${querySearchRegex}`;
            }

            const regex: RegExp = new RegExp(rule.type === 'string' ? Util.escapeRegExp(rule.searchValue) : rule.searchValue);
            const matchFound: boolean = regex.test(currentUrl);            

            if (matchFound) {
                if (rule.replaceUrl) {
                    isExternalUrl = true;
                    trackingParam = window.location.search.substring(1);
                }
                currentUrl = isExternalUrl ? rule.replaceUrl : currentUrl.replace(regex, rule.replaceValue);
            }

            return replace || matchFound;
        }, false);

        if (newURLFound) {
            let fullUrl: string = isExternalUrl ? `${currentUrl}` : `${baseUrl}${currentUrl}`;
            fullUrl = trackingParam ? fullUrl.includes('?') ? `${fullUrl}&${trackingParam}` : `${fullUrl}?${trackingParam}` : fullUrl;

            if (isExternalUrl) {
                if (!window.navigator.userAgent.includes('Prerender')) {
                    window.location.replace(fullUrl);
                }

                // do not resolve otherwise to block booting the app
            } else {
                window.history.replaceState(undefined, undefined, fullUrl);
                resolve();
            }

            seo.addPrerender301Tag(fullUrl);
        } else {
            resolve();
        }
    });
}

@NgModule({
    imports: [
        HttpClientModule,
        StoreModule.forRoot(reducers, { 
            metaReducers,
            runtimeChecks: {
                // turning these off for now since they are blocking activation flow
                // TODO: will discuss how we can turn them back in the future
                strictStateImmutability: false,
                strictActionImmutability: false
            }
        }),
        ModalModule,
        OverlayModule
    ],
    providers: [
        Api.CallbackApi,
        Api.CartApi,
        Api.CmsApi,
        Api.CreditCardApi,
        Api.HeroBannerApi,
        Api.HopApi,
        Api.InternationalRateApi,
        Api.LineApi,
        Api.LineBannerApi,
        Api.NdelApi,
        Api.ByodApi,
        Api.OrderApi,
        Api.PastDueApi,
        Api.AccountSummaryApi,
        Api.PaymentSummaryApi,
        Api.RequestStatusApi,
        Api.ReturnEligibilityApi,
        Api.ReturnApi,
        Api.StatementApi,
        Api.TransactionApi,
        Api.UsageApi,
        Api.UserApi,
        Api.OneTimePaymentApi,
        Api.QuickpaymentApi,
        Api.AdditionalDevicePaymentApi,
        Api.LevelMoveApi,
        Api.DiscountPromotion,
        Api.EsimWebsheetApi,
        Api.GlobalTravelPassApi,
        Api.IcopApi,
        CoreRoute,
        {
            provide: ErrorHandler,
            useClass: Service.XmErrorHandler
        },

        {
            provide: APP_INITIALIZER,
            useFactory: initRouterListeners,
            deps: [ Analytics, CoreRoute ],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: redirectOrReplaceUrl,
            deps: [ CONFIG_TOKEN, REWRITE_TOKEN, Service.Seo ],
            multi: true
        },
        { provide: HTTP_INTERCEPTORS, useClass: ApiResourceInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ShareRequestInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: GenericErrorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: CmsErrorInterceptor, multi: true }
    ]
})
export class CoreModule {}
